"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { useDevicePassedWidth } from "@/hooks/app/useDevicePassedWidth"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { TypeTourSearchResultItem } from "@/lib/redux/features/tour/types"
import { getQueryStringTourDetail } from "@/utils/string/getQueryStringTourDetail"
import { getTourRatingColor } from "@/utils/tour/getTourRatingColor"
import {
  getTourSocialProofKey,
  resolveTourRating,
} from "@/utils/tour/getTourRatingStatus"
import { formatTourHighlights } from "@/utils/tour/parseTourName"
import { faClock } from "@fortawesome/free-regular-svg-icons"
import { faChevronRight, faFire, faMapMarkedAlt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Card, Image, Modal } from "antd"
import { useParams } from "next/navigation"
import { FC, KeyboardEvent, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

const SOCIAL_PROOF_GUEST_COUNTS: Record<string, number> = {
  "tour.social_proof_15_24h": 15,
  "tour.social_proof_8_8h": 8,
  "tour.social_proof_10_8h": 10,
  "tour.social_proof_20_24h": 20,
  "tour.social_proof_6_4h": 6,
  "tour.social_proof_15_24h_alt": 15,
}

const DISPLAY_PRICE_MARKUP = 1.05

type TourItemProps = {
  type?: "card" | "default"
  tour: TypeTourSearchResultItem
}

const TourItem: FC<TourItemProps> = ({ type = "default", tour }) => {
  const params = useParams()
  const { i18n, t } = useTranslation()
  const { getFormatCurrency, getValidDayjs } = useTETranslation()
  const isMobile = useDevicePassedWidth((width) => width <= 768)
  const [highlightsOpen, setHighlightsOpen] = useState(false)

  const rating = useMemo(() => resolveTourRating(tour.id), [tour.id])
  const averageColor = getTourRatingColor(rating.score)
  const socialProofKey = useMemo(() => getTourSocialProofKey(tour.id), [tour.id])

  const tourUrl = `/${i18n.language}/tours/detail/${tour.id}?${getQueryStringTourDetail(tour, `${params.categoryId || ""}`)}`

  const isCard = type === "card"

  const splitedName = useMemo(() => {
    return (tour?.tourName || "").split("|").reduce((arr: string[], str) => {
      if (str === tour.tourName) {
        return [str]
      }
      return arr.concat([str])
    }, [])
  }, [tour?.tourName])

  const highlightsText = useMemo(
    () => formatTourHighlights(tour?.hightLights || ""),
    [tour?.hightLights],
  )

  const salePrice = parseFloat(tour.priceDefault)
  const listPrice = Number.isFinite(salePrice)
    ? Math.round(salePrice * DISPLAY_PRICE_MARKUP)
    : null
  const showStrikethrough =
    Number.isFinite(salePrice) && listPrice !== null && listPrice > salePrice

  const openHighlights = (event?: { preventDefault: () => void; stopPropagation: () => void }) => {
    event?.preventDefault()
    event?.stopPropagation()
    setHighlightsOpen(true)
  }

  const handleHighlightsKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      event.stopPropagation()
      setHighlightsOpen(true)
    }
  }

  const renderRating = () => (
    <div className={`tour-item__rating tour-item__rating--${averageColor}`}>
      <span className="tour-item__rating-score">{rating.score.toFixed(1)}</span>
      <span className="tour-item__rating-label">{t(rating.labelKey)}</span>
      <span className="tour-item__rating-count">
        {t("tour.reviews_count", { count: rating.reviewCount })}
      </span>
    </div>
  )

  const renderTourName = () => (
    <Title
      ellipsis={isMobile ? { rows: 2, tooltip: true } : { rows: 3, tooltip: true }}
      level={4}
      className="tour-item__title"
      style={{ width: "100%" }}
    >
      {splitedName[0]}
    </Title>
  )

  const renderMeta = () => (
    <ul className="tour-item__meta">
      <li className="tour-item__meta-item">
        <FontAwesomeIcon icon={faClock} className="tour-item__meta-icon" aria-hidden />
        <span>
          {tour.totalDay} {t("common.day")} {tour.totalNight} {t("common.night")}
        </span>
      </li>
      {!!tour.departure && (
        <li className="tour-item__meta-item">
          <FontAwesomeIcon icon={faMapMarkedAlt} className="tour-item__meta-icon" aria-hidden />
          <span>{t("tour.departure_from_city", { city: tour.departure })}</span>
        </li>
      )}
    </ul>
  )

  const renderHighlights = () => {
    if (!highlightsText) return null
    return (
      <div
        className="tour-item__highlights-preview"
        role="button"
        tabIndex={0}
        onClick={openHighlights}
        onKeyDown={handleHighlightsKeyDown}
      >
        <p className="tour-item__highlights-preview-text">{highlightsText}</p>
        <span className="tour-item__highlights-preview-link">{t("common.see_detail")}</span>
      </div>
    )
  }

  const renderPriceSection = () => (
    <>
      <p className="tour-item__social-proof-banner">
        <FontAwesomeIcon icon={faFire} className="tour-item__social-proof-icon" aria-hidden />
        {t(socialProofKey, { count: SOCIAL_PROOF_GUEST_COUNTS[socialProofKey] })}
      </p>
      <div className="tour-item__price-block">
        <Text type="secondary" className="tour-item__price-label">
          {t("tour.price_only_from")}
        </Text>
        {showStrikethrough && (
          <p className="tour-item__price-old">{getFormatCurrency(listPrice!)}</p>
        )}
        <p className="tour-item__price-sale">
          {getFormatCurrency(salePrice)}
        </p>
      </div>
      <p className="tour-item__departure-note">
        {t("tour.departure_on_date", {
          date: getValidDayjs(tour.startDate).format("DD/MM"),
        })}
      </p>
    </>
  )

  const renderCta = () => (
    <span className="tour-item__cta">
      <span className="tour-item__cta-label">{t("tour.view_detail")}</span>
      <FontAwesomeIcon icon={faChevronRight} className="tour-item__cta-icon" aria-hidden />
    </span>
  )

  const renderAside = () => (
    <aside className="tour-item__aside">
      <p className="tour-item__social-proof-banner">
        <FontAwesomeIcon icon={faFire} className="tour-item__social-proof-icon" aria-hidden />
        {t(socialProofKey, { count: SOCIAL_PROOF_GUEST_COUNTS[socialProofKey] })}
      </p>
      <div className="tour-item__aside-bottom">
        <div className="tour-item__price-block">
          <Text type="secondary" className="tour-item__price-label">
            {t("tour.price_only_from")}
          </Text>
          {showStrikethrough && (
            <p className="tour-item__price-old">{getFormatCurrency(listPrice!)}</p>
          )}
          <p className="tour-item__price-sale">
            {getFormatCurrency(salePrice)}
          </p>
        </div>
        <p className="tour-item__departure-note">
          {t("tour.departure_on_date", {
            date: getValidDayjs(tour.startDate).format("DD/MM"),
          })}
        </p>
        {renderCta()}
      </div>
    </aside>
  )

  const renderMain = () => (
    <div className="tour-item__main">
      {renderRating()}
      {renderTourName()}
      {renderMeta()}
      {renderHighlights()}
    </div>
  )

  const renderMedia = () => (
    <div className="tour-item__media">
      <div className="tour-item__image-root">
        <Image
          preview={false}
          className="tour-item__image-el"
          alt={tour.image?.name || "default image tour"}
          src={tour.image?.path || "/static/images/tour/default_image.png"}
        />
      </div>
    </div>
  )

  const listLayout = isCard ? (
    <div className="tour-item tour-item--card">
      {renderMedia()}
      <div className="tour-item__body">
        {renderRating()}
        {renderTourName()}
        {renderMeta()}
        {renderHighlights()}
        {renderPriceSection()}
        {renderCta()}
      </div>
    </div>
  ) : (
    <div className="tour-item tour-item--list">
      {renderMedia()}
      {renderMain()}
      {renderAside()}
    </div>
  )

  const card = (
    <Card
      hoverable
      styles={{ body: { padding: 0 } }}
      className={`tour-item__container${isCard ? " tour-item__container--card" : ""}`}
    >
      {listLayout}
    </Card>
  )

  return (
    <>
      <a
        href={tourUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="tour-item__link"
      >
        {card}
      </a>
      <Modal
        open={highlightsOpen}
        title={t("tour.highlights")}
        footer={null}
        width={760}
        destroyOnHidden
        className="tour-item__highlights-modal"
        onCancel={() => setHighlightsOpen(false)}
      >
        <div className="tour-item__highlights-modal-body">{highlightsText}</div>
      </Modal>
    </>
  )
}

export default TourItem
